import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions, Autocomplete, Grid2 as Grid, Box } from "@mui/material";
import React from "react";
import useDebugInformation from "../../../../hooks/useDebugInformation";
import { api, ui } from "../../../../interfaces/interfaces";
import { useAppSelector } from "@store/hooks";
import JobsTable, { Data } from "./JobsTable";
import { RightioWBSAPIModelsSPSpRFTrackFastSearchGetSearchResult } from "@utils/apis/wbs/endpoints";
import { inputComponentLogger } from "@utils/general";

const name = "JobSearchModal"

const handleClose = () => {
    ui.JobSearchModal = false;
    ui.JobsSearchTerm = '';
    ui.TimeFrame = {
        "value": 2160,
        "label": "3 Months"
    }
};

const convertData = (data: RightioWBSAPIModelsSPSpRFTrackFastSearchGetSearchResult[]): Data[] => {
    return data.map(d => ({
        jobId: d.id || -1,
        accountType: d.accountType || "",
        jobDescription: d.jobDescription || "",
        status: d.statusDescription || "",
        customer: d.name || "",
        address: `${d.street}${d.county ? d.county + ", " : ""}${d.town}` || "",
        postcode: d.postcode || "",
        dateRequired: new Date(d.dateTimeRequired || 0)?.toISOString().split('T')[0],
    }))
}

const handleSearch = async (setSearch: React.Dispatch<React.SetStateAction<Data[]>>, term: string, timeframe?: { value: number, label: string }) => {
    const results = await api.jobsSearch({ hours: timeframe?.value ?? 0, search: term })

    results && setSearch(convertData(results))
};

type Props = {
    parent: string
}

const timeFrames = [
    {
        value: 24,
        label: "24h"
    },
    {
        value: 24 * 3,
        label: "3 Days"
    },
    {
        value: 24 * 7,
        label: "1 Week"
    },
    {
        value: 24 * 14,
        label: "2 Weeks"
    },
    {
        value: 24 * 30,
        label: "1 Month"
    },
    {
        value: 24 * 60,
        label: "2 Months"
    },
    {
        value: 24 * 90,
        label: "3 Months"
    },
    {
        value: 24 * 180,
        label: "6 Months"
    },
    {
        value: 24 * 365,
        label: "1 Year"
    },
    {
        value: 24 * 365 * 10,
        label: "Everything"
    },
]

const JobSearchModal: React.FC<Props> = ({ parent }) => {
    const show = useAppSelector(({ ui }) => ui.JobSearchModal)
    const searchTerm = useAppSelector(({ ui }) => ui.JobsSearchTerm)
    const timeFrame = useAppSelector(({ ui }) => ui.TimeFrame)
    const [search, setSearch] = React.useState<Data[]>([])

    useDebugInformation(name, { parent })
    return (
        (<Dialog open={show} onClose={handleClose} maxWidth="lg" fullWidth
            BackdropProps={{
                sx: {
                    backgroundColor: "rgb(104 104 104 / 94%)"
                }
            }}
        >
            <DialogTitle>Jobs Search</DialogTitle>
            <Box noValidate
                component="form"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    m: 'auto',
                    width: 'fit-content',
                    minHeight: 540,
                    minWidth: "100%",
                }}>
                <DialogContent dividers>
                    <DialogContentText>
                        Please enter the surname of the customer, job number, postcode or the engineer number to search.
                    </DialogContentText>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={9}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Search"
                                type="text"
                                fullWidth
                                variant="standard"
                                value={searchTerm}
                                onChange={(e) => { ui.JobsSearchTerm = e.target.value }}
                                onBlur={(e) =>
                                    inputComponentLogger(`${parent}▷[${name}]`, e.target.value)
                                }
                            />
                        </Grid>
                        <Grid size={1}>
                            <Button variant="contained" onClick={() => handleSearch(setSearch, searchTerm, timeFrame)}>Search</Button>
                        </Grid>

                        <Grid size={2}>
                            <Autocomplete
                                disablePortal
                                id="combo-box-demo"
                                options={timeFrames}
                                renderInput={(params) => <TextField {...params} label="Timeframe" />}
                                value={timeFrame || null}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                onChange={(e, value) => { value && (ui.TimeFrame = value) }}
                            />
                        </Grid>
                        <Grid size={12}>
                            {search.length > 0 && <JobsTable data={search} />}
                        </Grid>
                    </Grid>

                </DialogContent>
            </Box>
            <DialogActions>
                <Button color="error" variant="contained" onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog >)
    );
}

JobSearchModal.displayName = name;

export default React.memo(JobSearchModal);